import React, {useCallback, useRef, useState} from 'react';
import TextField from '@mui/material/TextField';
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteChangeReason,
  AutocompleteProps,
  Box,
  InputBaseComponentProps,
  OutlinedInputProps,
  Tooltip,
} from '@mui/material';

import {useEditPanelContext} from '../../components/AdminDataGrid/EditPanelContext';
import {TEntity} from '../../../../models/entity';
import {EditorModus} from '../../components/EditDialog/EditDialog';
import {FormInputEventHandler, isEmptyValue, markRequiredLabelRed, showRequiredMark,} from '../../../utils/formUtils';
import {redRequiredLabel} from '../TextField/TextField';
import clsx from 'clsx';
import {ResponsivePopper, useAutocompleteStyles} from '../Autocomplete/Autocomplete';

export type CustomSelectProps<TOption, TValue> = Omit<AutocompleteProps<TOption, false, boolean | undefined, false>,
    'renderInput' | 'options' | 'getOptionLabel' | 'onChange' | 'value' | 'isOptionEqualToValue'> & {
  required?: boolean;
  value: TValue | null;
  label?: string;
  validateFunction?: (fieldValue: any, key: string) => boolean | undefined;
  disableValidation?: boolean;
  options: TOption[]
  getDisplayName(option: TOption): string;
  getPopperOptionLabel?(option: TOption): string;
  className?: string;
  name: string;
  displayEmpty?: boolean;
  includeNone?: boolean;
  emptyOption?: TOption;
  disabled?: boolean;
  fullWidth?: boolean;
  isShowErrorTooltip?: boolean;
  InputProps?: Partial<OutlinedInputProps>;
  inputProps?: InputBaseComponentProps ;
  onChange?: (e: FormInputEventHandler) => void;
  onNativeChange?: AutocompleteProps<TOption, false, boolean | undefined, false>['onChange'],
  mode?: EditorModus;
  getKey?: (value: TOption) => any;
  getValue?: (value: TOption) => TValue;
  plainValue?: boolean;
  isOptionEqualToValue?: (option: TOption, value: TValue) => boolean;
  error?: string;
  overrideBlankValue?: boolean;
  validateName?: string;
  checkIsValueEmpty?: (value: TValue) => boolean;
};

export default function CustomSelect<TOption, TValue = TOption>(props: CustomSelectProps<TOption, TValue>) {
  const {
    disablePortal = true,
    fullWidth = true,
    options: inputOptions,
    disableValidation,
    getPopperOptionLabel = props.getDisplayName,
    plainValue = true,
    getValue = (value: any) => plainValue ? value?.id : value,
    getKey = (option: any) => option?.id,
    overrideBlankValue = false,
    isOptionEqualToValue = (option, value: any) => getKey(option) === (plainValue ? value : getKey(value)),
    error,
    validateFunction,
    isShowErrorTooltip = false,
    label,
    onInputChange: baseOnInputChange,
    name,
    required,
    filterOptions,
    includeNone = !required,
    displayEmpty = includeNone,
    emptyOption = includeNone ? {} as TOption : undefined,
    title,
    disabled,
    InputProps,
    inputProps,
    onChange, 
    onNativeChange,
    getDisplayName,
    mode,
    ...other
  } = props;

  const [open, setOpen] = useState(false);
  const { autocompleteStyle } = useAutocompleteStyles();


  const { validateField, mode: contextMode,  } = useEditPanelContext<TEntity>();
  const handleLoseFocus = useCallback(
    (e: React.FocusEvent<HTMLDivElement, Element>) => {
      if ((!validateField && !validateFunction) || !required) return;
      if (validateField) validateField(props.value, name);
      if (validateFunction) validateFunction(props.value, name);
    },
    [validateField, validateFunction, required, name, props.value]
  );

  function getOptionsWithValue(): TOption[] {
    if (emptyOption && !filterOptions) return [emptyOption, ...inputOptions];
    return inputOptions;
  }

  const currOptions = getOptionsWithValue();
  const inputRef = useRef<HTMLInputElement>(null);

  function isOptionEqualToNativeValue(option: TOption, value: TOption): boolean{
    if(typeof value === 'string' && value.length === 0) return getValue(option) === value;
    if(!props.isOptionEqualToValue) return getKey(option) === getKey(value);
    return isOptionEqualToValue(option, getValue(value));
  }

  function handleChange(e: React.SyntheticEvent<Element, Event>, value: TOption | NonNullable<TOption> | null, reason: AutocompleteChangeReason) {
    if(onNativeChange) {
      onNativeChange(e, value, reason);
      return;
    }
    else onChange?.({target: {name, value: value ? getValue(value) : null}});
  }


  const { requiredLabelStyle } = redRequiredLabel();

  function getNativeValue() {
    if(isEmptyValue(other.value) && !overrideBlankValue) return null;
    const item = currOptions.find(i => isOptionEqualToValue(i, other.value!));
    return item ?? null;
  }

  function getDefaultValue() {
    if(isEmptyValue(other.value) && !includeNone) return currOptions[0];
    return null;
  }

  return (
      <MuiAutocomplete<TOption, false, boolean | undefined, false>
          {...other}
          value={getNativeValue()}
          defaultValue={other.defaultValue ?? getDefaultValue()}
          disabled={
              mode === EditorModus.Vis || contextMode === EditorModus.Vis || disabled
          }
          fullWidth={fullWidth}
          getOptionLabel={(opt: any) => !isEmptyValue(opt) ? getDisplayName(opt) : ''}
          onOpen={(evt) => {
            setOpen(true);
          }}
          onClose={(prop) => {
            setOpen(false);
          }}
          disablePortal={disablePortal}
          multiple={false}
          disableClearable={true}
          open={open}
          onBlur={(e) => handleLoseFocus(e)}
          isOptionEqualToValue={isOptionEqualToNativeValue}
          openText=""
          closeText=""
          options={currOptions}
          PopperComponent={(props) => (
              <ResponsivePopper {...props} inputRef={inputRef}/>
          )}
          componentsProps={{
            paper: {
              sx: {
                '& .MuiAutocomplete-listbox': {
                  'maxHeight': '20vh !important'
                }
              }
            }
          }}
          onChange={handleChange}
          renderOption={(params: any, option: any) => {
            const label = !isEmptyValue(option) ? getPopperOptionLabel(option) : '';
            return (
                <Box
                    component="li"
                    {...params}
                    key={`_${getKey(option) ?? params.key}`}
                    color={option?.filtered ? 'rgba(0, 0, 0, 0.55)' : undefined}
                >
                  {label?.length === 0 && displayEmpty ? (
                      <Box display="block" minHeight={'1.2em'} key={'empty_opt'}>{''}</Box>
                  ) : (
                      label
                  )}
                </Box>
            );
          }}
          renderInput={(params) => {
            return (
                <Tooltip title={isShowErrorTooltip ? error ?? '' : title ?? ''}>
                  <TextField
                      {...params}
                      ref={inputRef}
                      disabled={
                          ((mode === EditorModus.Vis || contextMode === EditorModus.Vis) &&
                              !disableValidation) ||
                          disabled
                      }
                      required={
                          !disableValidation &&
                          showRequiredMark(other.value, mode, error, required)
                      }
                      error={!!error}
                      label={label}
                      className={clsx(
                          other.className,
                          autocompleteStyle,
                          InputProps?.className,
                          markRequiredLabelRed(other.value, mode ?? contextMode)
                              ? requiredLabelStyle
                              : ''
                      )}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        ...InputProps,
                        ...params.InputProps,
                        inputProps: {
                          ...params.inputProps,
                          ...inputProps,
                          readOnly: true
                        },
                        fullWidth: fullWidth,
                      }}
                  />
                </Tooltip>
            );
          }}
      />
  );
}

export function ConvertEmptyStringToNull(value: any) {
  if ((value as string).length === 0) return null;
  return value;
}

